import moment from 'moment';
import { BreachPathSideMenuDataType } from '../types';
import { html, TemplateResult } from 'lit';
import { AffectedDeviceType, DEVICES_CLASS, OS_CLASS } from 'apps/webcomponents/src/riskManagement/common/constants';
import { BaseWebComponentTranslator } from 'apps/webcomponents/src/framework/BaseWebComponentTranslator';
import { FontSizes, FontWeights } from 'apps/webcomponents/src/types/common/types';
import { OS_TYPE } from 'apps/webcomponents/src/riskManagement/common/constants';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { breachPathIssueNameMapping } from '../../common/utils';
import { BreachPathIssueNameKey } from '../../common/types';
import { getBulletColorForScore } from 'apps/webcomponents/src/riskManagement/common/utils';
import { getTextForScore } from 'apps/webcomponents/src/riskManagement/common/customRenderers';

function renderLabeledContentWithEllipsisCopy(value: string): TemplateResult {
  return html`
    <div class="w-full d-flex align-items-center copy-labeled-content">
      <gz-tooltip
        class="flex-1"
        isEllipsis
        tooltipText="${value}"
      >
        <span>${value}</span>
      </gz-tooltip>
      <gz-copy-to-clipboard
        class="copy-icon"
        confirmationMessage="${translateFn('Copied')}"
        tooltipText="${translateFn('Copy to clipboard')}"
        clipboardMessage="${value}"
      ></gz-copy-to-clipboard>
    </div>
  `;
}

export function nameRenderer(data: BreachPathSideMenuDataType): string {
  return breachPathIssueNameMapping[data.name] ?? breachPathIssueNameMapping[BreachPathIssueNameKey.Unknown];
}

export function cveRenderer(data: BreachPathSideMenuDataType): string {
  return `${data.cveCount ?? '-'}`;
}

export function mainResourceNameRenderer(data: BreachPathSideMenuDataType): TemplateResult {
  return renderLabeledContentWithEllipsisCopy(data.mainResource.resourceName);
}

export function affectedResourcesRenderer(data: BreachPathSideMenuDataType): number {
  return data.affectedResources.length;
}

export function lastScannedOnRenderer(data: BreachPathSideMenuDataType): string {
  const date = data.updatedAt;
  return date ? moment(date).format('DD MMM YYYY, HH:mm') : '-';
}

export function firstFoundOnRenderer(data: BreachPathSideMenuDataType): string {
  const date = data.createdAt;
  return date ? moment(date).format('DD MMM YYYY, HH:mm') : '-';
}

export function resourceNameRenderer(data: BreachPathSideMenuDataType): TemplateResult {
  return renderLabeledContentWithEllipsisCopy(data.mainResource.resourceName);
}

export function riskScoreRenderer(data: BreachPathSideMenuDataType): TemplateResult | string {
  if (!data.scoreBreakdown) {
    return '-';
  }

  return html`
    <gz-bullet color="${getBulletColorForScore(data.scoreBreakdown.finalScore)}">
      <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}" style="color: var(--main-titles-inputs-600);">
        ${getTextForScore(data.scoreBreakdown.finalScore)} (${data.scoreBreakdown.finalScore}%)
      </gz-text>
    </gz-bullet>
  `;
}

export function resourceIdRenderer(data: BreachPathSideMenuDataType): TemplateResult {
  return renderLabeledContentWithEllipsisCopy(data.mainResource.resourceId);
}

export function resourceTypeRenderer(data: BreachPathSideMenuDataType): TemplateResult | string {
  if (!data.mainResource.resourceType || data.mainResource.resourceType === AffectedDeviceType.NOT_AFFECTED) {
    return '-';
  }

  return html`
    <div class="content-with-icon">
      <gz-breach-path-graph-icon
        name="${DEVICES_CLASS.get(data.mainResource.resourceType)}"
        color="var(--chart-302-icon-buttons-titles)"
      ></gz-breach-path-graph-icon>
      <gz-tooltip
        isEllipsis
        tooltipText="${BaseWebComponentTranslator.getInstance().getDeviceTypes(data.mainResource.resourceType)}"
      >
        <gz-text
          class="d-inline"
          fontSize="${FontSizes.PARAGRAPH_12}"
          fontWeight="${FontWeights.NORMAL}"
          style="color: var(--main-titles-inputs-600);"
        >
          ${BaseWebComponentTranslator.getInstance().getDeviceTypes(data.mainResource.resourceType)}
        </gz-text>
      </gz-tooltip>
    </div>
  `;
}

export function platformRenderer(data: BreachPathSideMenuDataType): TemplateResult | string {
  if (!data.operatingSystem || data.operatingSystem === OS_TYPE.UNKNOWN) {
    return '-';
  }

  return html`
    <div class="content-with-icon">
      <gz-breach-path-graph-icon
        name="ds-${OS_CLASS.get(data.operatingSystem)}"
        color="var(--chart-302-icon-buttons-titles)"
      ></gz-breach-path-graph-icon>
      <gz-tooltip
        isEllipsis
        tooltipText="${BaseWebComponentTranslator.getInstance().getOperatingSystem(data.operatingSystem)}"
      >
        <gz-text
          class="d-inline"
          fontSize="${FontSizes.PARAGRAPH_12}"
          fontWeight="${FontWeights.NORMAL}"
          style="color: var(--main-titles-inputs-600);"
        >
          ${BaseWebComponentTranslator.getInstance().getOperatingSystem(data.operatingSystem)}
        </gz-text>
      </gz-tooltip>
    </div>
  `;
}

export function cloudAccountRenderer(data: BreachPathSideMenuDataType): TemplateResult {
  return renderLabeledContentWithEllipsisCopy(data.cloudAccount);
}

export function regionRenderer(data: BreachPathSideMenuDataType): string {
  return data.region;
}
